// SIPP-729: Story Mode Screen 3 — Test Strip Results by Category
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { WaterTestKitResultsSelector } from '../../../store/waterTestKitResults/waterTestKitResultsSelector'
import { WaterTestParameterResult } from '../../../types/WaterTestStripResultDocument'

// ── helpers ─────────────────────────────────────────────────────────────────

type StatusLabel = 'High' | 'Caution' | 'Low' | 'Good' | 'NA'

const STATUS_PRIORITY: Record<string, number> = {
  High: 3,
  Caution: 2,
  Low: 2,
  Good: 1,
  NA: 0,
}

const worstStatus = (items: (WaterTestParameterResult & { category: string })[]): StatusLabel => {
  let worst: StatusLabel = 'Good'
  let worstPriority = 0
  for (const item of items) {
    const p = STATUS_PRIORITY[item.label] ?? 0
    if (p > worstPriority) {
      worstPriority = p
      worst = item.label as StatusLabel
    }
  }
  return worst
}

type StatusConfig = {
  label: string
  colorKey: 'success' | 'warning' | 'error'
}

const STATUS_MAP: Record<string, StatusConfig> = {
  High: { label: 'High', colorKey: 'error' },
  Caution: { label: 'Caution', colorKey: 'warning' },
  Low: { label: 'Caution', colorKey: 'warning' },
  Good: { label: 'Good', colorKey: 'success' },
}

// ── status pill ─────────────────────────────────────────────────────────────

const StatusPill = ({ status }: { status: StatusLabel }) => {
  const theme = useTheme()
  const config = STATUS_MAP[status]

  if (!config) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          py: 0.5,
          borderRadius: 99,
          bgcolor: theme.palette.grey[100],
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: theme.palette.grey[400],
          }}
        />
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: theme.palette.grey[500],
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          N/A
        </Typography>
      </Box>
    )
  }

  const palette = theme.palette[config.colorKey]

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.5,
        py: 0.5,
        borderRadius: 99,
        bgcolor: palette.light,
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: palette.main,
        }}
      />
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          color: palette.dark,
          lineHeight: 1,
          whiteSpace: 'nowrap',
        }}
      >
        {config.label}
      </Typography>
    </Box>
  )
}

// ── main screen ──────────────────────────────────────────────────────────────

export const StoryScreen3 = () => {
  const theme = useTheme()
  const grouped = useSelector(WaterTestKitResultsSelector.makeLatestResultGroupedByParamGroup)

  const groups = useMemo(() => {
    if (!grouped) return []
    return Object.entries(grouped).map(([name, group]: [string, any]) => ({
      name,
      count: group.items.length,
      status: worstStatus(group.items),
    }))
  }, [grouped])

  const hasGroups = groups.length > 0

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        p: 4,
        gap: 3,
        '@keyframes fadeInUp': {
          from: { opacity: 0, transform: 'translateY(24px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        animation: 'fadeInUp 0.5s ease-out both',
      }}
    >
      <Box>
        <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 1 }}>
          Your Test Strip Results
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          This data comes from the physical test strip you submitted. Results are grouped by
          category, with each group showing the worst status among its parameters.
        </Typography>
      </Box>

      {hasGroups ? (
        <Stack spacing={1.5}>
          {groups.map((group) => (
            <Box
              key={group.name}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: '8px',
                px: '16px',
                py: '14px',
              }}
            >
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                  {group.name}
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {group.count} parameter{group.count !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <StatusPill status={group.status} />
            </Box>
          ))}
        </Stack>
      ) : (
        <Box
          sx={{
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: '8px',
            p: 3,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            No test strip results yet. Complete a test to see your readings here.
          </Typography>
        </Box>
      )}
    </Box>
  )
}
